import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Mark } from '../components/Layout';
import { ErrorNote, Field, LanguageSwitch } from '../components/ui';
import { post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTenant } from '../lib/tenant';
import { Link } from 'react-router-dom';
import { toAsciiDigits } from '../lib/format';

type Choice = { ticket: string; schoolName: string; schoolNameAr: string | null; role: string };

export function Login() {
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const tenant = useTenant();
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [mode, setMode] = useState<'phone' | 'email'>('phone');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let r: { choose?: Choice[] };
      if (mode === 'email') r = await post('/api/auth/login', { email, password, locale: i18n.language });
      else if (!codeSent) {
        await post('/api/auth/otp/request', { phone });
        setCodeSent(true);
        return;
      } else r = await post('/api/auth/otp/verify', { phone, code, locale: i18n.language });
      // The same person at several schools: ask which one
      if (r.choose) return setChoices(r.choose);
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const pick = async (c: Choice) => {
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/pick', { ticket: c.ticket, locale: i18n.language });
      await refresh();
    } catch (err) {
      setError(err);
      setChoices(null);
    } finally {
      setBusy(false);
    }
  };

  const ar = i18n.language === 'ar';
  const school = tenant.school;
  const schoolName = school ? (ar && school.nameAr) || school.name : null;

  return (
    <div className="login">
      <div className="login-lang">
        <LanguageSwitch />
      </div>
      <section className="login-card">
        <div className="login-brand">
          {school?.logoUrl ? <img className="login-logo" src={school.logoUrl} alt="" /> : <Mark />}
          <div>
            <h1>{schoolName ?? t('app.name')}</h1>
            <p className="login-tagline">{schoolName ? tenant.platformName : t('app.tagline')}</p>
          </div>
        </div>
        <p className="login-lede">{t('auth.lede')}</p>

        {choices ? (
          <div className="stack">
            <h2>{t('auth.chooseSchool')}</h2>
            <p className="muted">{t('auth.chooseHint')}</p>
            {choices.map((c) => (
              <button key={c.ticket} type="button" className="btn btn-block choice" disabled={busy} onClick={() => void pick(c)}>
                <span>{(ar && c.schoolNameAr) || c.schoolName}</span>
                <span className="muted small">{t(`role.${c.role}`)}</span>
              </button>
            ))}
            <button type="button" className="btn-quiet" onClick={() => setChoices(null)}>
              {t('common.back')}
            </button>
          </div>
        ) : (
          <>

        <div className="segmented" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'phone'} onClick={() => (setMode('phone'), setError(null))}>
            {t('auth.withPhone')}
          </button>
          <button type="button" role="tab" aria-selected={mode === 'email'} onClick={() => (setMode('email'), setError(null))}>
            {t('auth.withEmail')}
          </button>
        </div>

        <form onSubmit={submit} className="stack">
          {mode === 'email' ? (
            <>
              <Field label={t('auth.email')}>
                <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" />
              </Field>
              <Field label={t('auth.password')}>
                <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" />
              </Field>
            </>
          ) : (
            <>
              <Field label={t('auth.phone')} hint={t('auth.phoneHint')}>
                <input type="tel" autoComplete="tel" required value={phone} onChange={(e) => (setPhone(e.target.value), setCodeSent(false))} dir="ltr" placeholder="+971 50 123 4567" />
              </Field>
              {codeSent && (
                <Field label={t('auth.code')} hint={t('auth.codeSent')}>
                  <input inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required value={code} onChange={(e) => setCode(toAsciiDigits(e.target.value).replace(/\D/g, ''))} dir="ltr" className="code-input" />
                </Field>
              )}
            </>
          )}
          <ErrorNote error={error} />
          <button className="btn btn-primary btn-block" disabled={busy}>
            {mode === 'phone' && !codeSent ? t('auth.sendCode') : t('auth.signIn')}
          </button>
        </form>
          </>
        )}
        <p className="login-foot">
          {tenant.kind === 'platform' ? (
            <Link to="/">{t('auth.aboutPlatform', { name: tenant.platformName })}</Link>
          ) : (
            <Link to="/">{t('welcome.back', { school: schoolName })}</Link>
          )}
        </p>
      </section>
    </div>
  );
}
